'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface NavbarProps {
  onScrollTo: (section: string) => void;
}

export default function Navbar({ onScrollTo }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 glass-nav`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 group">
            <img
              alt="Scalio AI Agency"
              className="logo-img"
              src="/assets/images/ChatGPT_Image_Apr_5__2026__05_26_18_PM-1779730470887.png"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {['Services', 'Results', 'Process', 'About'].map((item) => (
              <button
                key={item}
                onClick={() => onScrollTo(item.toLowerCase())}
                className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 font-medium tracking-wide"
              >
                {item}
              </button>
            ))}
            <Link
              href="/ideas"
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200 font-semibold tracking-wide flex items-center gap-1.5"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              Idea Generator
            </Link>
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://calendly.com/creatormaska/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="glow-button inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white"
            >
              Book a Call
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden text-neutral-400 hover:text-white transition-colors p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 12h16" />
              <path d="M4 18h16" />
              <path d="M4 6h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <div className={`mobile-nav md:hidden glass-nav-solid ${mobileOpen ? 'open' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 pb-4 flex flex-col gap-3">
          {['Services', 'Results', 'Process', 'About'].map((item) => (
            <button
              key={item}
              onClick={() => { onScrollTo(item.toLowerCase()); setMobileOpen(false); }}
              className="text-left py-2.5 text-sm text-neutral-300 hover:text-white transition-colors border-b border-white/[0.05] font-medium"
            >
              {item}
            </button>
          ))}
          <Link
            href="/ideas"
            onClick={() => setMobileOpen(false)}
            className="py-2.5 text-sm text-blue-400 hover:text-blue-300 transition-colors border-b border-white/[0.05] font-semibold flex items-center gap-1.5"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            Idea Generator
          </Link>
          <a
            href="https://calendly.com/creatormaska/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="glow-button mt-2 flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white"
          >
            Book a Call
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </nav>
  );
}
